import React, { useEffect, useState, useMemo, useContext } from "react";
import { EARLY_PAY, discountBase, saving, earlyPayFigures } from "helpers/earlyPay";
import EarlyPayBanner from "components/EarlyPayBanner";
import H5 from "components/typography/H5";
import { Box, Flex, Grid, Spinner } from "theme-ui";
import { themevals } from "theme/themevals";
import H2 from "components/typography/H2";
import P from "components/typography/P";
import { ShareIcon, DownloadIcon, CheckMarkIcon, PlaneIcon, BankIcon, CreditCardIcon } from "components/svg";
import BasicButton from "components/forms/BasicButton";
import moment from "moment-timezone";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import { useToast } from "components/toast";
import { emailInvoice, getInvoicePDF, createPaymentIntent, updateInvoice } from "api/Invoices";
import { setInvoiceSurcharge, setPurchaseOrderSurcharge, FUEL_SURCHARGE_TYPE } from "api/FuelSurcharge";
import { getProjectPhotos } from "api/Projects";
import { getMapSnapshots } from "api/MulchMap";
import SnapshotIncludePicker from "components/SnapshotIncludePicker";
import ProjectPhotoIncludePicker from "components/ProjectPhotoIncludePicker";
import TermsEditor from "components/TermsEditor";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { Form, Formik } from "formik";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import Span from "components/typography/Span";
import { createPayment } from "api/Invoices";
import PlaidLinkButton from "components/PlaidLink";
import { exchangePublicToken, createACHPayment } from "api/Plaid";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { trackActivity } from "api/ActivityTracker";
import { getSavedPaymentMethods, chargeSavedMethod, createSetupIntent, saveCard, saveCardFromPayment, saveBankAccount, saveBankAccountFromPayment } from "api/Payments";
import UserContext from "context/User";
import { getInvoiceStatusDisplay, HoverTip } from "components/ui/InvoiceStatusBadge";

const Overview = ({ isCompany, company, invoice, reloadInvoice }: { isCompany: boolean; company: any; invoice: any; reloadInvoice: () => void }) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [items, setItems] = useState<any[]>([]);
  const toast = useToast();
  // Fuel surcharge removal panel: reason (required) + "the provider agreed
  // not to charge it - also remove it from the PO".
  const [fuelRemove, setFuelRemove] = useState<{ reason: string; alsoPo: boolean; busy: boolean } | null>(null);
  const [sendSubmitting, setSendSubmitting] = useState<boolean>(false);
  const [downloadSubmitting, setDownloadSubmitting] = useState<boolean>(false);
  const [projectPhotos, setProjectPhotos] = useState<any[]>([]);
  const [ccIsOpen, setCCIsOpen] = useState<boolean>(false);
  const [achIsOpen, setACHIsOpen] = useState<boolean>(false);
  const [intent, setIntent] = useState<any>(null);
  const [canOpenCCModal, setCanOpenCCModal] = useState<boolean>(false);
  const [ccSubmitting, setCCSubmitting] = useState<boolean>(false);
  const [achSubmitting, setACHSubmitting] = useState<boolean>(false);
  const [canOpenACHModal, setCanOpenACHModal] = useState<boolean>(false);
  const [achPending, setACHPending] = useState<boolean>(false);
  const [paymentType, setPaymentType] = useState<any>("PAY_IN_FULL");
  const [paymentTypeReadonly, setPaymentTypeReadonly] = useState(false);

  // Add additional state for ACH flow
  const [processorToken, setProcessorToken] = useState<string | null>(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState<any>(null);
  const ACH_FEE = 5.0; // $5 flat fee

  // Saved payment methods state
  const [savedMethods, setSavedMethods] = useState<any[]>([]);
  const [savedMethodsLoading, setSavedMethodsLoading] = useState<boolean>(false);
  const [savedMethodPayModalOpen, setSavedMethodPayModalOpen] = useState<boolean>(false);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState<any>(null);
  const [savedMethodPaySubmitting, setSavedMethodPaySubmitting] = useState<boolean>(false);
  const [saveCardChecked, setSaveCardChecked] = useState<boolean>(false);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState<"ach" | "card" | null>(null);

  // Rush order detection: use project flag if available, fallback to computing from serviceDate
  const isRushOrder = (() => {
    if (invoice?.project?.isRushOrder !== undefined) return invoice.project.isRushOrder === true;
    // Fallback: compute from quote serviceDate vs quote acceptedDate (or project createdAt)
    const serviceDate = invoice?.quote?.serviceDate || invoice?.project?.serviceDate;
    const createdAt = invoice?.project?.createdAt;
    if (serviceDate && createdAt) {
      const svc = new Date(serviceDate);
      const created = new Date(createdAt);
      const diffDays = (svc.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
      return diffDays >= 0 && diffDays <= 5;
    }
    return false;
  })();
  // N2 - a client on the deposit requirement is not offered the
  // early-payment incentive (mutually exclusive with the deposit).
  // Component-level constant, folded into every discount-eligibility check
  // below, mirroring how isRushOrder is applied. Reads the billed client
  // (primaryClient is the sub-client of a company client).
  const quoteRequiresDeposit = !!(invoice as any)?.quote?.requiresDeposit;
  const depositIsRequired =
    quoteRequiresDeposit ||
    !!(invoice as any)?.client?.requiresDeposit ||
    !!(invoice as any)?.primaryClient?.requiresDeposit;
  // Name kept for the existing discount checks below: true means "no
  // early-payment incentive" - a required deposit (client-wide or on this
  // quote) or the incentive switched off on the quote.
  const clientRequiresDeposit = depositIsRequired || !!(invoice as any)?.quote?.earlyPaymentDiscountOff;
  // The deposit a deposit-required client owes up front (e.g. 50% for a new
  // customer). Offered as its own payment option and selected by default until
  // the first payment lands. Before this, a deposit-required client could only
  // pay 100% (GMI-100566, 2026-10-01).
  const requiredDepositPercent = (() => {
    const raw = Number(
      (quoteRequiresDeposit ? (invoice as any)?.quote?.depositPercentage : null) ??
        (invoice as any)?.client?.depositPercentage ??
        (invoice as any)?.primaryClient?.depositPercentage ??
        50,
    );
    return raw > 0 && raw < 100 ? raw : 50;
  })();
  const requiredDepositOpen =
    depositIsRequired && !(Array.isArray(invoice?.payments) && invoice.payments.length > 0);
  const [saveBankChecked, setSaveBankChecked] = useState<boolean>(false);
  const [saveCardSubmitting, setSaveCardSubmitting] = useState<boolean>(false);

  // Session 28 hotfix 6 (Phase 3): snapshot inclusion is now persisted
  // on `invoice.mulchMapSnapshotIds`. The picker component handles
  // the selection UI; frontend just tells the picker how many exist.
  const [snapshotCount, setSnapshotCount] = useState<number>(0);

  const { user } = useContext<any>(UserContext);
  const isClientAdmin = user?.client?.companyAdmin === true;

  const columnHelper = createColumnHelper();
  const stripe = useStripe();
  const elements = useElements();
  console.log(invoice);

  const toggleAchModal = () => {
    setCanOpenACHModal(true);
    setACHIsOpen(true);
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Overview";
  }, []);

  useEffect(() => {
    if (intent && canOpenCCModal) {
      setCCIsOpen(true);
      setCanOpenCCModal(false);
    }
  }, [canOpenCCModal, intent]);

  // Fetch saved methods on mount (only for client users)
  useEffect(() => {
    if (!isCompany) {
      _fetchSavedMethods();
    }
  }, [isCompany]);

  const _fetchSavedMethods = async () => {
    setSavedMethodsLoading(true);
    try {
      const methods = await getSavedPaymentMethods();
      setSavedMethods(Array.isArray(methods) ? methods : []);
    } catch (e: any) {
      console.error("Error fetching saved methods:", e);
    } finally {
      setSavedMethodsLoading(false);
    }
  };

  const toggleCCModal = async () => {
    setCCSubmitting(true);
    const res = await createPaymentIntent({ id: invoice.id, paymentType: paymentType });
    if (res.total) {
      setIntent(res);
      setCanOpenCCModal(true);
      setCCSubmitting(false);
    }
  };

  useEffect(() => {
    if (invoice) {
      const flatItems = invoice.items?.flat() || [];
      setItems(flatItems);
      updatePaymentTypeStateFromInvoice();
    }
  }, [invoice]);

  // Session 25 Batch #4 / Session 28 hotfix 6: fetch snapshot count for
  // the attached map so the picker chip only appears when there’s
  // something to include. Clears when the map is detached.
  // Session 28 hotfix 11: gated on `isCompany` — the `getMapSnapshots`
  // endpoint is company-only, so clients would 403 and get bounced.
  useEffect(() => {
    if (!isCompany) {
      setSnapshotCount(0);
      return;
    }
    const mulchMapId = invoice && invoice.mulchMapId;
    if (!mulchMapId) {
      setSnapshotCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const snapshots = await getMapSnapshots(mulchMapId);
      if (cancelled) return;
      const count = Array.isArray(snapshots) ? snapshots.length : 0;
      setSnapshotCount(count);
    })();
    return () => { cancelled = true; };
  }, [isCompany, invoice && invoice.mulchMapId]);

  const updatePaymentTypeStateFromInvoice = () => {
    if (!invoice) return;
    
    // If there are payments, force pay in full and readonly
    if (Array.isArray(invoice.payments) && invoice.payments.length > 0) {
      setPaymentType("PAY_IN_FULL");
      setPaymentTypeReadonly(true);
      return;
    }

    // Deposit-required client, nothing paid yet: the required deposit is the
    // default, and the client may still choose to pay in full.
    if (requiredDepositOpen) {
      setPaymentType("REQUIRED_DEPOSIT");
      setPaymentTypeReadonly(false);
      return;
    }

    // Check if within discount window
    if (!invoice.quote?.acceptedDate) {
      // No accepted date, force pay in full and readonly
      setPaymentType("PAY_IN_FULL");
      setPaymentTypeReadonly(true);
      return;
    }
    
    const baseDate = moment(invoice.quote.acceptedDate);
    const expiryDate = baseDate.clone().add(5, 'days');
    const now = moment();
    const isWithinDiscountWindow = now.isBefore(expiryDate);

    // If no due date AND within discount window AND meets minimum AND not rush, allow deposit selection
    const invoiceTotal = invoice?.items?.flat()?.reduce((acc: number, item: any) => acc + item.price * item.quantity, 0) || 0;
    const meetsMinimum = invoiceTotal >= 2000;
    // isRushOrder is computed at component level with fallback

    if (!invoice.dueDate && isWithinDiscountWindow && meetsMinimum && !isRushOrder && !clientRequiresDeposit) {
      setPaymentTypeReadonly(false);
      return;
    }

    // If discount already applied, allow selection
    if (invoice.discount) {
      setPaymentTypeReadonly(false);
      return;
    }

    // NEW: If invoice has allowPartialPayment enabled, allow selection
    if (invoice.allowPartialPayment) {
      setPaymentTypeReadonly(false);
      return;
    }

    // Otherwise, force pay in full and readonly (outside discount window or has due date)
    setPaymentType("PAY_IN_FULL");
    setPaymentTypeReadonly(true);
  };

  useEffect(() => {
    _getProjectPhotos();
  }, []);

  const _getProjectPhotos = async () => {
    const res = await getProjectPhotos({ data: invoice.project });
    setProjectPhotos(res);
  };

  // Handle Plaid success
  const _handlePlaidSuccess = async (publicToken: string, metadata: any) => {
    setACHPending(true);
    try {
      // Exchange the public token for a processor token
      const result = await exchangePublicToken({
        publicToken,
        accountId: metadata.account_id,
      });

      if (result?.processorToken) {
        setProcessorToken(result.processorToken);
        setSelectedBankAccount({
          name: metadata.institution.name,
          mask: metadata.account.mask, // Last 4 digits of account number
          type: metadata.account.subtype || metadata.account.type,
          // Store plaid metadata for optional save
          _plaidPublicToken: publicToken,
          _plaidAccountId: metadata.account_id,
        });
        setACHPending(false);

        // Close the connect bank modal and open the confirmation modal
        setACHIsOpen(false);
        setConfirmationModalOpen(true);
      } else {
        toast.add("Failed to connect bank account", "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("Plaid exchange error:", error);
      toast.add("Failed to connect bank account", "var(--theme-ui-colors-danger)");
    }
  };

  const _submitACHPayment = async (values: any) => {
    setACHSubmitting(true);
    try {
      const discountDetails = getEarlyPaymentDiscountDetails();
      const paymentValue = discountDetails?.amountToCharge ?? Number(values.amount) ?? 0;

      const update = {
        invoiceId: invoice.id,
        processorToken: processorToken as string,
        value: paymentValue,
        reference: values.reference,
        invoiceSequence: invoice.sequence,
        payer: { id: invoice.client.id },
        type: "INVOICE",
        fees: ACH_FEE,
        paymentMethod: "PLAID_ACH",
        transactionDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        paymentPlan: paymentType,
      };
      const res = await createACHPayment(update);

      if (res?.success) {
        // Optionally save the bank account for future use
        if (saveBankChecked && res?.bankAccountId) {
          try {
            await saveBankAccountFromPayment({
              bankAccountId: res.bankAccountId,
              ownerType: "user",
            });
            toast.add("Bank account saved for future payments.", "var(--theme-ui-colors-success)");
            _fetchSavedMethods();
          } catch (saveErr: any) {
            console.error("Error saving bank account:", saveErr);
            // Don't block the payment success flow
          }
        }

        // Check if the payment requires additional action (e.g., microdeposit verification)
        if (res?.status === 'requires_action' || res?.status === 'requires_source_action') {
          setACHIsOpen(false);
          setConfirmationModalOpen(false);
          setACHSubmitting(false);
          setSaveBankChecked(false);
          toast.add(
            `ACH payment initiated. You may need to verify your bank account via microdeposits. Check your email for instructions.`,
            "var(--theme-ui-colors-warning)"
          );
          reloadInvoice();
        } else if (res?.status === 'processing' || res?.status === 'succeeded') {
          setACHIsOpen(false);
          setConfirmationModalOpen(false);
          setACHSubmitting(false);
          setSaveBankChecked(false);
          toast.add(`ACH payment submitted successfully.`, "var(--theme-ui-colors-success)");
          reloadInvoice();
        } else {
          // Handle other statuses
          setACHIsOpen(false);
          setConfirmationModalOpen(false);
          setACHSubmitting(false);
          setSaveBankChecked(false);
          toast.add(`ACH payment initiated. Status: ${res?.status || 'pending'}`, "var(--theme-ui-colors-success)");
          reloadInvoice();
        }
      } else {
        setACHSubmitting(false);
        toast.add(`Error submitting ACH payment.`, "var(--theme-ui-colors-danger)");
      }
    } catch (error) {
      console.error("ACH payment error:", error);
      setACHSubmitting(false);
      toast.add(`Error submitting ACH payment.`, "var(--theme-ui-colors-danger)");
    }
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "itemName",
      cell: (info: any) => <Box sx={{ color: "#000", fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Item</span>,
      enableSorting: false,
      meta: {
        style: { width: "200px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.description, {
      id: "description",
      cell: (info: any) => <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>{info.getValue()}</Box>,
      header: () => <span>Description</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.quantity, {
      id: "quantity",
      cell: (info: any) => {
        const row = info.row.original;
        const showUnit = row.type !== "service"; // Only show unit if not service

        return (
          <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>
            {info.getValue()} {showUnit && row.unit}
          </Box>
        );
      },
      header: () => <span>Qty</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.price, {
      id: "rate",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500" }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Box>;
      },
      header: () => <span>Rate</span>,
      enableSorting: false,
    }),
    columnHelper.accessor((row: any) => row?.price * row?.quantity, {
      id: "total",
      cell: (info: any) => {
        const value = info.getValue();
        return <Box sx={{ color: themevals.colors.dkgrey, fontWeight: "500", textAlign: "right" }}>{value?.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Box>;
      },
      header: () => (
        <Flex sx={{ width: "100%", alignItems: "center", justifyContent: "flex-end" }}>
          <Box>Amount</Box>
        </Flex>
      ),
      enableSorting: false,
    }),
  ];

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.add("Link copied to clipboard.", "var(--theme-ui-colors-success)");
  };

  const handleEmailInvoice = async () => {
    setSendSubmitting(true);
    // Session 28 hotfix 6: backend reads invoice.mulchMapSnapshotIds.
    const res = await emailInvoice({ data: { id: invoice.id } });
    if (res.success) {
      toast.add("Invoice emailed successfully.", "var(--theme-ui-colors-success)");

    } else {
      toast.add("Failed to email quote.", "var(--theme-ui-colors-danger)");
    }
    reloadInvoice();
    setSendSubmitting(false);
  };

  const handleDownloadInvoice = async () => {
    setDownloadSubmitting(true);
    // Session 28 hotfix 6: backend reads invoice.mulchMapSnapshotIds.
    const res = await getInvoicePDF({ data: { id: invoice.id } });
    
    void trackActivity({
      module: "invoices",
      action: "generate_invoice_pdf",
      actionType: "create",
      userId: invoice?.lastUpdateBy?.id || null,
      companyId: company?.id || null,
      entityType: "invoice",
      entityId: invoice.id,
      metadata: {},
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });

    const link = document.createElement("a");
    link.href = res.result;
    link.download = `GMI_${String(invoice.sequence).padStart(5, "0")}_invoice.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadSubmitting(false);
  };

  const _submitCCPayment = async () => {
    if (!stripe || !elements || !intent?.clientSecret) {
      toast.add("Stripe is not ready", "var(--theme-ui-colors-danger)");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    // If "Save card" is checked, we need to do SetupIntent flow AFTER the payment
    const { error, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: invoice?.client?.name || "Customer",
          email: invoice?.client?.billingEmail || undefined,
        },
      },
      setup_future_usage: saveCardChecked ? "off_session" : undefined,
    });

    if (error) {
      toast.add(error.message || "Payment failed", "var(--theme-ui-colors-danger)");
      return;
    }

    if (paymentIntent.status === "succeeded") {
      const payment = {
        paymentMethod: "CREDIT_CARD",
        value: intent.baseTotal,
        type: "INVOICE",
        fees: intent.fees,
        transactionId: paymentIntent.id,
        transactionDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        payer: { id: invoice.client.id },
        source: "stripe",
        paymentPlan: paymentType,
      };

      const res = await createPayment({ id: invoice.id, data: payment });
      console.log("Payment response:", res);
      toast.add("Payment successful!", "var(--theme-ui-colors-success)");

      // If save card was checked, save the payment method from the PaymentIntent
      if (saveCardChecked && paymentIntent.id) {
        setSaveCardSubmitting(true);
        try {
          const saveRes = await saveCardFromPayment({
            paymentIntentId: paymentIntent.id,
            ownerType: "user",
          });
          if (saveRes?.id) {
            toast.add("Card saved for future payments.", "var(--theme-ui-colors-success)");
            _fetchSavedMethods();
          }
        } catch (saveErr: any) {
          console.error("Error saving card:", saveErr);
          // Don't block the payment success flow
        } finally {
          setSaveCardSubmitting(false);
        }
      }

      reloadInvoice();
      setCCIsOpen(false);
      setSaveCardChecked(false);
    } else {
      toast.add("Payment processing failed", "var(--theme-ui-colors-danger)");
    }
  };

  // ─── Pay with Saved Method ───
  const _openSavedMethodPayModal = (method: any) => {
    setSelectedSavedMethod(method);
    setSavedMethodPayModalOpen(true);
  };

  const _submitSavedMethodPayment = async () => {
    if (!selectedSavedMethod) return;
    setSavedMethodPaySubmitting(true);
    try {
      const isCard = selectedSavedMethod.methodType === "card";
      const discountDetails = getEarlyPaymentDiscountDetails();
      const effectiveAmount = discountDetails?.amountToCharge ?? amountDue;
      const fee = isCard ? +(effectiveAmount * 0.03).toFixed(2) : ACH_FEE;

      const res = await chargeSavedMethod({
        paymentMethodRecordId: selectedSavedMethod.id,
        invoiceId: invoice.id,
        invoiceSequence: invoice.sequence,
        value: effectiveAmount,
        fees: fee,
        payer: { id: invoice.client.id },
        type: "INVOICE",
        paymentMethod: isCard ? "CREDIT_CARD" : "PLAID_ACH",
        transactionDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        paymentPlan: paymentType,
      });

      if (res?.success) {
        toast.add("Payment submitted successfully!", "var(--theme-ui-colors-success)");
        setSavedMethodPayModalOpen(false);
        setSelectedSavedMethod(null);
        reloadInvoice();
      } else {
        toast.add(res?.message || "Payment failed.", "var(--theme-ui-colors-danger)");
      }
    } catch (err: any) {
      console.error("Saved method payment error:", err);
      toast.add("Error submitting payment.", "var(--theme-ui-colors-danger)");
    } finally {
      setSavedMethodPaySubmitting(false);
    }
  };

  // Calculate values up front
  const total = invoice?.items?.flat()?.reduce((acc: number, item: any) => acc + item.price * item.quantity, 0) || 0;
  const roundedTotal = +total.toFixed(2);
  const discountPercent =
    invoice?.discountAmount && roundedTotal > 0
      ? +(((invoice.discountAmount || 0) / roundedTotal) * 100).toFixed(2)
      : invoice?.discount?.amount
      ? Number(invoice.discount.amount)
      : 0;
  const discountAmount = discountPercent ? +((roundedTotal * discountPercent) / 100).toFixed(2) : 0;
  const totalPaid = invoice?.payments?.reduce((acc: number, payment: any) => acc + payment.value, 0) || 0;
  const baseTotal = invoice?.discountTotal && invoice.discountTotal > 0 ? invoice.discountTotal : roundedTotal;
  const amountDue = Math.max(0, +(baseTotal - totalPaid).toFixed(2));

  const getEarlyPaymentDiscountDetails = () => {
    if (!invoice || !invoice.quote?.acceptedDate) return null;
    
    const baseDate = moment(invoice.quote.acceptedDate);
    const expiryDate = baseDate.clone().add(5, "days");
    const now = moment();
    const isWithinDiscountWindow = now.isBefore(expiryDate);

    // Check all eligibility criteria
    const invoiceTotal = invoice?.items?.flat()?.reduce((acc: number, item: any) => acc + item.price * item.quantity, 0) || 0;
    const meetsMinimum = invoiceTotal >= 2000;
    if (invoice.dueDate || !isWithinDiscountWindow || !meetsMinimum || isRushOrder || clientRequiresDeposit || invoice.discountAmount || totalPaid > 0) {
      return null;
    }

    // Saving on the goods only; the fuel surcharge is never discounted.
    const discountPercentValue = paymentType === "DEPOSIT" ? EARLY_PAY.depositPct : EARLY_PAY.fullPct;
    const discountAmountValue = saving(discountBase(invoice?.items), discountPercentValue);
    const discountedTotal = +(total - discountAmountValue).toFixed(2);
    const amountToCharge =
      paymentType === "DEPOSIT" ? +(discountedTotal / 2).toFixed(2) : discountedTotal;

    return {
      discountPercent: discountPercentValue,
      discountAmount: discountAmountValue,
      discountedTotal,
      amountToCharge,
    };
  };

  const paymentOptions = useMemo(() => {
    const options = [
      {
        label: "Pay In Full",
        value: "PAY_IN_FULL",
      },
    ];

    // Check if within discount window and eligible
    let isWithinDiscountWindow = false;
    if (invoice?.quote?.acceptedDate) {
      const baseDate = moment(invoice.quote.acceptedDate);
      const expiryDate = baseDate.clone().add(5, 'days');
      const now = moment();
      isWithinDiscountWindow = now.isBefore(expiryDate);
    }
    const invoiceTotal = invoice?.items?.flat()?.reduce((acc: number, item: any) => acc + item.price * item.quantity, 0) || 0;
    const meetsMinimum = invoiceTotal >= 2000;
    const discountEligible = isWithinDiscountWindow && meetsMinimum && !isRushOrder && !clientRequiresDeposit;

    // Add 50% deposit option if within discount window and eligible
    if (discountEligible && !invoice?.dueDate && (!invoice?.discountAmount || invoice.discountAmount === 0)) {
      options.push({
        label: "50% Deposit",
        value: "DEPOSIT",
      });
    }

    // Required deposit for deposit-required clients (backend: REQUIRED_DEPOSIT)
    if (requiredDepositOpen) {
      options.push({
        label: `${requiredDepositPercent}% Deposit (required)`,
        value: "REQUIRED_DEPOSIT",
      });
    }

    // Add custom deposit option if allowPartialPayment is enabled
    if (invoice?.allowPartialPayment) {
      const percent = invoice.partialPaymentPercent || 75;
      options.push({
        label: `${percent}% Deposit`,
        value: "CUSTOM_DEPOSIT",
      });
    }

    return options;
  }, [invoice]);

  const handlePaymentTypeChange = async (val: any) => {
    setPaymentType(val);
    setCCSubmitting(true);
    const res = await createPaymentIntent({ id: invoice.id, paymentType: val });
    if (res.total) {
      setIntent(res);
    }
    setCCSubmitting(false);
  };

  const handleCCModalToggle = () => {
    setCCIsOpen((prev) => {
      if (prev) {
        setPaymentType("PAY_IN_FULL");
        setPaymentTypeReadonly(false);
        updatePaymentTypeStateFromInvoice();
        setSaveCardChecked(false);
      }
      return !prev;
    });
  };

  const handleACHPaymentTypeChange = (val: any, setFieldValue: (name: string, val?: any) => void) => {
    setPaymentType(val);
    setFieldValue("amount", getACHInitialAmount(val));
  };

  const getACHInitialAmount = (type = paymentType) => {
    // Calculate base total (after discount)
    const baseTotal =
      invoice?.discountTotal && invoice.discountTotal > 0 ? invoice.discountTotal : invoice?.items?.flat()?.reduce((acc: number, item: any) => acc + item.price * item.quantity, 0) || 0;

    // Subtract payments
    const totalPaid = invoice?.payments?.reduce((acc: number, payment: any) => acc + (payment.value || 0), 0) || 0;
    let amountDue = Math.max(0, +(baseTotal - totalPaid).toFixed(2));

    // Handle partial payment types
    if (type === "DEPOSIT") {
      // Standard 50% deposit (within discount window)
      amountDue = +(amountDue / 2).toFixed(2);
    } else if (type === "REQUIRED_DEPOSIT") {
      amountDue = +(amountDue * (requiredDepositPercent / 100)).toFixed(2);
    } else if (type === "CUSTOM_DEPOSIT") {
      // Custom partial payment percentage (outside discount window)
      const percent = invoice?.partialPaymentPercent || 75;
      amountDue = +(amountDue * (percent / 100)).toFixed(2);
    }
    return amountDue;
  };

  const handleACHConfirmationModalToggle = () => {
    setConfirmationModalOpen((prev) => {
      if (prev) {
        setPaymentType("PAY_IN_FULL");
        setPaymentTypeReadonly(false);
        updatePaymentTypeStateFromInvoice();
        setSaveBankChecked(false);
      }
      return !prev;
    });
  };

  // ─── Helper: Format saved method display ───
  const formatMethodLabel = (method: any) => {
    if (method.methodType === "card") {
      const brand = (method.brand || "Card").charAt(0).toUpperCase() + (method.brand || "card").slice(1);
      return `${brand} ending in ${method.last4}`;
    }
    return `${method.bankName || "Bank"} account ending in ${method.last4}`;
  };

  // ─── "View/Pay Invoice" landing (2026-10-08, Jack) ───
  // The accepted-quote popup links here with ?pay=1. A client lands on a
  // "How would you like to pay?" chooser with the same options the popup
  // showed (100% / 50% with savings, or Net 30), each payable by card or
  // ACH, instead of having to find the Pay buttons themselves.
  const [payChooserOpen, setPayChooserOpen] = useState(false);
  const [payFocus, setPayFocus] = useState<string | null>(null);

  // From the project's Financial Info: ?fuel=remove opens the remove box,
  // ?fuel=add just brings the Add button into view (it still takes a click).
  useEffect(() => {
    if (!isCompany || !invoice?.id) return;
    const params = new URLSearchParams(window.location.search);
    const intent = params.get("fuel");
    if (!intent) return;
    params.delete("fuel");
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    if (intent === "remove") setFuelRemove({ reason: "", alsoPo: false, busy: false });
    setTimeout(() => document.getElementById("fuel-surcharge-control")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [isCompany, invoice?.id]);
  useEffect(() => {
    if (isCompany || !invoice?.id || amountDue <= 0) return;
    const params = new URLSearchParams(window.location.search);
    const pay = params.get("pay");
    if (pay === "1" || pay === "full" || pay === "deposit") {
      // ?pay=full / ?pay=deposit come from a saving picked in the
      // quote-accepted window: open the chooser on just that option.
      setPayFocus(pay === "full" ? "PAY_IN_FULL" : pay === "deposit" ? "DEPOSIT" : null);
      setPayChooserOpen(true);
      params.delete("pay");
      const qs = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [isCompany, invoice?.id, amountDue]);

  const chooserRows = paymentOptions.map((o: any) => {
    const eligible = paymentOptions.some((x: any) => x.value === "DEPOSIT");
    let amount: number | null = null;
    let save: number | null = null;
    const f = earlyPayFigures(total, discountBase(invoice?.items));
    if (o.value === "PAY_IN_FULL") {
      amount = eligible ? f.fullTotal : amountDue;
      save = eligible ? f.fullSave : null;
    } else if (o.value === "DEPOSIT") {
      amount = f.depositNow;
      save = f.depositSave;
    } else if (o.value === "REQUIRED_DEPOSIT") {
      amount = +((amountDue * Number(requiredDepositPercent || 0)) / 100).toFixed(2);
    }
    return { ...o, amount, save, eligible };
  });

  // Step 2 of the chooser: when the client has saved methods of the kind
  // they picked, list them (as the Pay via ACH / Card buttons do) instead
  // of going straight to a new card / Plaid (Jack, 2026-10-08).
  const [chooserPick, setChooserPick] = useState<{ type: string; kind: "card" | "ach" } | null>(null);
  const savedOfKind = (kind: "card" | "ach") =>
    savedMethods.filter((m: any) => m.methodType === (kind === "card" ? "card" : "us_bank_account"));
  const chooseKind = (type: string, kind: "card" | "ach") => {
    if (savedOfKind(kind).length > 0) {
      setChooserPick({ type, kind });
    } else if (kind === "card") {
      payByCard(type);
    } else {
      payByAch(type);
    }
  };
  const payWithSaved = (type: string, method: any) => {
    setPayChooserOpen(false);
    setChooserPick(null);
    setPaymentType(type);
    _openSavedMethodPayModal(method);
  };

  const payByCard = async (type: string) => {
    setPayChooserOpen(false);
    setPaymentType(type);
    setCCSubmitting(true);
    const res = await createPaymentIntent({ id: invoice.id, paymentType: type });
    setCCSubmitting(false);
    if (res?.total) {
      setIntent(res);
      setCanOpenCCModal(true);
    } else {
      toast.add(res?.message || "Could not start the card payment.", "var(--theme-ui-colors-danger)");
    }
  };
  const payByAch = (type: string) => {
    setPayChooserOpen(false);
    setPaymentType(type);
    toggleAchModal();
  };

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Overview
      </H5>
      {/* Early-payment reminder until the window closes (2026-10-08). */}
      {!isCompany && invoice?.id && amountDue > 0 && (
        <EarlyPayBanner
          key={`${invoice.id}-${totalPaid}-${invoice?.discountAmount ?? 0}`}
          invoiceId={invoice.id}
          onPay={() => setPayChooserOpen(true)}
        />
      )}
      {/* Not in Zoho Books yet (2026-10-08, GMI-00049): say why instead of
          leaving staff to discover it in Zoho. */}
      {isCompany && invoice?.id && invoice?.zohoSynced === false && Number(invoice?.status) !== 4 && (
        <Box sx={{ maxWidth: "1000px", width: "100%", margin: "0 auto 14px auto", padding: "10px 14px", borderRadius: "6px", background: "#FAEEDA", color: "#633806", fontFamily: "Poppins", fontSize: "13px" }}>
          {invoice?.clientIsLead
            ? `Not in Zoho Books yet: ${invoice?.client?.name ?? "this client"} is still a lead. Convert the lead to a client (with "Create the Zoho Books customer now" ticked) and this invoice syncs automatically.`
            : "Not in Zoho Books yet. It syncs on the next save; if it still doesn't appear, check the client has a Zoho customer."}
        </Box>
      )}
      <Flex sx={{ maxWidth: "1000px", width: "100%", margin: "0 auto 20px auto", alignItems: "flex-end", flexWrap: "wrap", gap: "10px" }}>
        <Flex sx={{ gap: "20px", flex: 1, alignItems: "center" }}>
          <Box sx={{ cursor: "pointer" }} onClick={() => handleCopyLink()}>
            <ShareIcon fill={themevals.colors.dkgrey} width="20px" />
          </Box>
          {isCompany && (
            <Box sx={{ cursor: "pointer" }} onClick={handleEmailInvoice}>
              {sendSubmitting && <Spinner size={20} color={themevals.colors.primary} />}
              {!sendSubmitting && <PlaneIcon fill={themevals.colors.dkgrey} width="20px" />}
            </Box>
          )}
          <Box sx={{ cursor: "pointer" }} onClick={() => handleDownloadInvoice()}>
            {downloadSubmitting && <Spinner size={20} color={themevals.colors.primary} />}
            {!downloadSubmitting && <DownloadIcon fill={themevals.colors.dkgrey} width="20px" />}
          </Box>
          {snapshotCount > 0 && isCompany && invoice?.id && invoice?.mulchMapId && (
            <SnapshotIncludePicker
              recordType="invoice"
              recordId={invoice.id}
              mulchMapId={invoice.mulchMapId}
              initialSelectedIds={
                Array.isArray(invoice.mulchMapSnapshotIds)
                  ? invoice.mulchMapSnapshotIds
                  : []
              }
            />
          )}
          {/* A18 — project photos fold into the same invoice include
              surface as the site-map snapshot picker. Staff only, and
              only when the invoice's project actually has photos (the
              picker self-hides at zero). Drives each photo's existing
              invoiceInclude flag; the PDF renders them inline (A19). */}
          {isCompany && invoice?.project?.id && projectPhotos.length > 0 && (
            <ProjectPhotoIncludePicker
              projectId={invoice.project.id}
              photos={projectPhotos}
              onChanged={_getProjectPhotos}
            />
          )}
        </Flex>
        {!isCompany && amountDue > 0 && (
          <Flex sx={{ justifyContent: "flex-end", flex: 1, gap: "12px", alignItems: "center" }}>
            {/* \u2500\u2500\u2500 Pay via ACH Button + Popover \u2500\u2500\u2500 */}
            <Box sx={{ position: "relative" }}>
              <BasicButton
                scheme="secondaryOutline"
                type="button"
                onClick={() => {
                  const savedBanks = savedMethods.filter((m: any) => m.methodType === "us_bank_account");
                  if (savedBanks.length > 0) {
                    setPaymentDropdownOpen(paymentDropdownOpen === "ach" ? null : "ach");
                  } else {
                    toggleAchModal();
                  }
                }}
              >
                <BankIcon fill={themevals.colors.black} width="14px" height="14px" /> Pay via ACH
              </BasicButton>
              {paymentDropdownOpen === "ach" && (
                <>
                  <Box
                    onClick={() => setPaymentDropdownOpen(null)}
                    sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      right: 0,
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                      border: `1px solid ${themevals.colors.inputOutline}`,
                      zIndex: 10,
                      minWidth: ["280px", "320px"],
                      overflow: "hidden",
                    }}
                  >
                    {savedMethods.filter((m: any) => m.methodType === "us_bank_account").map((method: any) => {
                      const discountDetails = getEarlyPaymentDiscountDetails();
                      const effectiveAmount = discountDetails?.amountToCharge ?? amountDue;
                      const fee = ACH_FEE;
                      const totalWithFee = +(effectiveAmount + fee).toFixed(2);
                      return (
                        <Flex
                          key={method.id}
                          onClick={() => { setPaymentDropdownOpen(null); _openSavedMethodPayModal(method); }}
                          sx={{
                            alignItems: "center", justifyContent: "space-between", padding: "10px 14px",
                            cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" },
                            transition: "background-color 0.1s ease", gap: "12px",
                            borderBottom: `1px solid ${themevals.colors.inputOutline}`,
                          }}
                        >
                          <Flex sx={{ alignItems: "center", gap: "8px", flex: 1 }}>
                            <BankIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                            <Box>
                              <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.black} style={{ whiteSpace: "nowrap" }}>
                                {method.label || formatMethodLabel(method)}
                              </P>
                              <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" fontWeight="400" color={themevals.colors.dkgrey} style={{ whiteSpace: "nowrap" }}>
                                {method.ownerType === "org" ? "Organization" : "Personal"}
                                {` \u00B7 ${fee.toLocaleString("en-US", { style: "currency", currency: "USD" })} fee`}
                              </P>
                            </Box>
                          </Flex>
                          <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color={themevals.colors.black} style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                            {totalWithFee.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                          </P>
                        </Flex>
                      );
                    })}
                    <Flex
                      onClick={() => { setPaymentDropdownOpen(null); toggleAchModal(); }}
                      sx={{
                        alignItems: "center", gap: "8px", padding: "10px 14px", cursor: "pointer",
                        "&:hover": { backgroundColor: "#f5f5f5" }, transition: "background-color 0.1s ease",
                        borderBottom: `1px solid ${themevals.colors.inputOutline}`,
                      }}
                    >
                      <Box sx={{ width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: themevals.colors.primary, fontSize: "16px", fontWeight: "bold" }}>+</Box>
                      <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.primary}>
                        Connect a new bank account
                      </P>
                    </Flex>
                    <Flex
                      onClick={() => { setPaymentDropdownOpen(null); window.location.href = "/client-company?tab=payments"; }}
                      sx={{ alignItems: "center", justifyContent: "center", padding: "8px 14px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}
                    >
                      <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" fontWeight="400" color={themevals.colors.dkgrey}>
                        Manage payment methods
                      </P>
                    </Flex>
                  </Box>
                </>
              )}
            </Box>

            {/* \u2500\u2500\u2500 Pay via Credit Card Button + Popover \u2500\u2500\u2500 */}
            <Box sx={{ position: "relative" }}>
              <BasicButton
                scheme="secondaryOutline"
                type="button"
                onClick={() => {
                  const savedCards = savedMethods.filter((m: any) => m.methodType === "card");
                  if (savedCards.length > 0) {
                    setPaymentDropdownOpen(paymentDropdownOpen === "card" ? null : "card");
                  } else {
                    toggleCCModal();
                  }
                }}
                $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}
              >
                {!ccSubmitting && <CreditCardIcon fill={themevals.colors.black} width="14px" height="14px" />}
                {ccSubmitting && <Spinner size={14} color="#000" />}
                Pay via Credit Card
              </BasicButton>
              {paymentDropdownOpen === "card" && (
                <>
                  <Box
                    onClick={() => setPaymentDropdownOpen(null)}
                    sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9 }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      right: 0,
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                      border: `1px solid ${themevals.colors.inputOutline}`,
                      zIndex: 10,
                      minWidth: ["280px", "320px"],
                      overflow: "hidden",
                    }}
                  >
                    {savedMethods.filter((m: any) => m.methodType === "card").map((method: any) => {
                      const discountDetails = getEarlyPaymentDiscountDetails();
                      const effectiveAmount = discountDetails?.amountToCharge ?? amountDue;
                      const fee = +(effectiveAmount * 0.03).toFixed(2);
                      const totalWithFee = +(effectiveAmount + fee).toFixed(2);
                      return (
                        <Flex
                          key={method.id}
                          onClick={() => { setPaymentDropdownOpen(null); _openSavedMethodPayModal(method); }}
                          sx={{
                            alignItems: "center", justifyContent: "space-between", padding: "10px 14px",
                            cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" },
                            transition: "background-color 0.1s ease", gap: "12px",
                            borderBottom: `1px solid ${themevals.colors.inputOutline}`,
                          }}
                        >
                          <Flex sx={{ alignItems: "center", gap: "8px", flex: 1 }}>
                            <CreditCardIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                            <Box>
                              <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.black} style={{ whiteSpace: "nowrap" }}>
                                {method.label || formatMethodLabel(method)}
                              </P>
                              <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" fontWeight="400" color={themevals.colors.dkgrey} style={{ whiteSpace: "nowrap" }}>
                                {method.ownerType === "org" ? "Organization" : "Personal"}
                                {method.expiryMonth && method.expiryYear
                                  ? ` \u00B7 Exp ${String(method.expiryMonth).padStart(2, "0")}/${method.expiryYear}`
                                  : ""}
                                {` \u00B7 ${fee.toLocaleString("en-US", { style: "currency", currency: "USD" })} fee`}
                              </P>
                            </Box>
                          </Flex>
                          <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color={themevals.colors.black} style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                            {totalWithFee.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                          </P>
                        </Flex>
                      );
                    })}
                    <Flex
                      onClick={() => { setPaymentDropdownOpen(null); toggleCCModal(); }}
                      sx={{
                        alignItems: "center", gap: "8px", padding: "10px 14px", cursor: "pointer",
                        "&:hover": { backgroundColor: "#f5f5f5" }, transition: "background-color 0.1s ease",
                        borderBottom: `1px solid ${themevals.colors.inputOutline}`,
                      }}
                    >
                      <Box sx={{ width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: themevals.colors.primary, fontSize: "16px", fontWeight: "bold" }}>+</Box>
                      <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.primary}>
                        Use a new card
                      </P>
                    </Flex>
                    <Flex
                      onClick={() => { setPaymentDropdownOpen(null); window.location.href = "/client-company?tab=payments"; }}
                      sx={{ alignItems: "center", justifyContent: "center", padding: "8px 14px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}
                    >
                      <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" fontWeight="400" color={themevals.colors.dkgrey}>
                        Manage payment methods
                      </P>
                    </Flex>
                  </Box>
                </>
              )}
            </Box>
          </Flex>
        )}
      </Flex>

      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          margin: "0 auto 60px auto",
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          border: `1px solid ${themevals.colors.inputOutline}`,
          borderRadius: "6px",
          padding: "40px",
          backgroundColor: "#fff",
        }}
      >
        {/* 1. Header band — logo+company left, TITLE/#/AMOUNT DUE + status pill right */}
        <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "20px", paddingBottom: "20px", borderBottom: "1px solid #000" }}>
          <Flex sx={{ flexDirection: "column", flex: 1 }}>
            {company?.data?.logo && (
              <img src={company.data.logo} alt="Company Logo" style={{ width: "auto", height: "40px", marginBottom: "12px", alignSelf: "flex-start" }} />
            )}
            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="2px">
              {company.name}
            </P>
            {company.data?.address?.line1 && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {company.data.address.line1}
              </P>
            )}
            {company.data?.address?.line2 && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {company.data.address.line2}
              </P>
            )}
            {(company.data?.address?.city || company.data?.address?.state || company.data?.address?.zip) && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {`${company.data?.address?.city ?? ""}${company.data?.address?.city ? ", " : ""}${company.data?.address?.state ?? ""} ${company.data?.address?.zip ?? ""}`.trim()}
              </P>
            )}
          </Flex>
          <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
            <Box sx={{ fontFamily: "Rubik-Medium, Rubik, sans-serif", fontSize: "22px", letterSpacing: "1px", color: "#000", textTransform: "uppercase" }}>
              Invoice
            </Box>
            <P marginBottom="0px" marginTop="2px" fontFamily="Poppins" fontSize="11px" fontWeight="600" color={themevals.colors.dkgrey}>
              {`#GMI-${String(invoice.sequence).padStart(5, "0")}`}
            </P>
            {(() => {
              // Status pill mirrors backend PDF logic
              let pillText = ""; let pillFg = ""; let pillBg = "";
              if (invoice.status === 2 || amountDue <= 0) { pillText = "Paid"; pillFg = "#667E4E"; pillBg = "#DDE8D2"; }
              else if (invoice.status === 4) { pillText = "Voided"; pillFg = "#818181"; pillBg = "#EBEBEB"; }
              else if (invoice.dueDate && moment().isAfter(moment(invoice.dueDate)) && amountDue > 0) { pillText = "Overdue"; pillFg = "#A60000"; pillBg = "#F7DDDD"; }
              else if (invoice.status === 3) { pillText = "Partial Paid"; pillFg = "#CAA11C"; pillBg = "#FFFAEA"; }
              else if (invoice.status === 1) { pillText = "Unpaid"; pillFg = "#CAA11C"; pillBg = "#FFFAEA"; }
              if (!pillText) return null;
              return (
                <Box sx={{ marginTop: "8px", padding: "4px 10px", borderRadius: "12px", backgroundColor: pillBg, color: pillFg, fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "10px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                  {pillText}
                </Box>
              );
            })()}
            {/* GAP-41 (mockup 22) — "Submitted to Corrigo" status. Renders
             * once staff marks the invoice posted to Corrigo (backend
             * sets invoice.corrigoSubmittedAt). Shown to everyone,
             * including the client viewer, so a Corrigo-managed client
             * who checks JSCRM sees that we've pushed the invoice into
             * their system rather than seeing no signal at all. Absent
             * (null) for every non-Corrigo invoice, so nothing changes
             * for the ordinary flow. */}
            {(() => {
              // Early payment / Ready to bill: same rules as every other
              // status pill (components/ui/InvoiceStatusBadge). Hover for
              // what it means; clients get client wording.
              const early = getInvoiceStatusDisplay(invoice, {
                isStaff: user?.role !== null && user?.role !== undefined,
              });
              if (!early.tag) return null;
              return (
                <HoverTip tip={early.tag.title}>
                  <Box
                    sx={{ marginTop: "6px", padding: "4px 10px", borderRadius: "12px", backgroundColor: "#FFFAEA", color: "#8A6D0F", fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "10px", letterSpacing: "0.5px", textTransform: "uppercase" }}
                  >
                    {early.tag.text === "Early" ? "Early payment" : early.tag.text}
                  </Box>
                </HoverTip>
              );
            })()}
            {invoice?.corrigoSubmittedAt && (
              <Box
                title={`Submitted to Corrigo on ${moment(invoice.corrigoSubmittedAt).format("MMM D, YYYY")}`}
                sx={{ marginTop: "6px", padding: "4px 10px", borderRadius: "12px", backgroundColor: "#E4EAF2", color: "#3A5A80", fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "10px", letterSpacing: "0.5px", textTransform: "uppercase" }}
              >
                Submitted to Corrigo
              </Box>
            )}
            <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginTop: "14px" }}>
              Amount Due
            </Box>
            <P marginBottom="0px" marginTop="2px" fontFamily="Poppins" fontSize="20px" fontWeight="500" color="#000">
              {amountDue?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </P>
          </Flex>
        </Flex>

        {/* 2. Meta section — BILL TO (full postal) + invoice meta rows */}
        <Grid gap={0} columns={[1, "1fr 1fr"]} sx={{ width: "100%", marginTop: "24px", paddingBottom: "16px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
          <Flex sx={{ flexDirection: "column" }}>
            <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "4px" }}>
              Bill To
            </Box>
            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="2px" color="#000">
              {invoice.client?.name || "-"}
            </P>
            {invoice.client?.address?.line1 && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color="#000">
                {invoice.client.address.line1}
              </P>
            )}
            {invoice.client?.address?.line2 && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color="#000">
                {invoice.client.address.line2}
              </P>
            )}
            {(invoice.client?.address?.city || invoice.client?.address?.state || invoice.client?.address?.postalCode) && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color="#000">
                {`${invoice.client?.address?.city ?? ""}${invoice.client?.address?.city ? ", " : ""}${invoice.client?.address?.state ?? ""} ${invoice.client?.address?.postalCode ?? ""}`.trim()}
              </P>
            )}
            {(invoice.client?.billingEmail || invoice.client?.contactEmail) && (
              <P marginBottom="0px" marginTop="4px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {invoice.client?.billingEmail || invoice.client?.contactEmail}
              </P>
            )}
            {invoice.client?.phone && (
              <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                {invoice.client.phone}
              </P>
            )}
          </Flex>
          <Flex sx={{ flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
            <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
              <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                Invoice Date
              </Box>
              <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                {moment(invoice.createdAt).format("MMM D, YYYY")}
              </P>
            </Flex>
            {invoice.sentDate && (
              <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                  Sent
                </Box>
                <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                  {moment(invoice.sentDate).format("MMM D, YYYY")}
                </P>
              </Flex>
            )}
            {invoice.dueDate && (
              <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                  Due Date
                </Box>
                <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color={moment().isAfter(moment(invoice.dueDate)) && amountDue > 0 ? "#A60000" : "#000"}>
                  {moment(invoice.dueDate).format("MMM D, YYYY")}
                </P>
              </Flex>
            )}
            {invoice.project?.projectName && (
              <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                  Project
                </Box>
                <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                  {invoice.project.projectName}
                </P>
              </Flex>
            )}
            {invoice.quote?.sequence && (
              <Flex sx={{ flexDirection: "column", alignItems: "flex-end" }}>
                <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase" }}>
                  Quote #
                </Box>
                <P marginBottom="0px" marginTop="1px" fontFamily="Poppins" fontSize="11px" fontWeight="500" color="#000">
                  {`GMQ-${String(invoice.quote.sequence).padStart(5, "0")}`}
                </P>
              </Flex>
            )}
          </Flex>
        </Grid>

        {/* 3. Job Site — single-row callout */}
        {(invoice?.project?.projectName || invoice?.project?.location?.address) && (
          <Box sx={{ width: "100%", marginTop: "16px" }}>
            <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "4px" }}>
              Job Site
            </Box>
            <P marginBottom="0px" marginTop="0px" fontFamily="Poppins" fontSize="12px" fontWeight="500" color="#000">
              {[
                invoice?.project?.projectName,
                [invoice?.project?.location?.address?.line1, `${invoice?.project?.location?.address?.city ?? ""}, ${invoice?.project?.location?.address?.state ?? ""} ${invoice?.project?.location?.address?.postalCode ?? ""}`.trim()].filter(Boolean).join(" — "),
              ].filter(Boolean).join("  ·  ")}
            </P>
          </Box>
        )}

        {/* 4. Line items table */}
        <Box sx={{ width: "100%", marginTop: "24px" }}>
          {items && <TableBasic data={items} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} disableSorting />}
          {/* Fuel surcharge switch: staff, unpaid invoices (0 created /
              1 sent, nothing paid). Was [0, 4] - 4 is VOIDED, and a sent
              invoice had no switch (2026-10-08). The backend refuses the
              same cases and re-syncs Zoho. The project's Financial Info
              links here with ?fuel=remove|add. */}
          {isCompany && [0, 1].includes(Number(invoice?.status)) && !(totalPaid > 0) && (() => {
            const hasFuel = (items ?? []).some((i: any) => i?.type === FUEL_SURCHARGE_TYPE);
            const addBack = async () => {
              const r: any = await setInvoiceSurcharge(invoice.id, true);
              if (r?.error) return toast.add(r.error, "var(--theme-ui-colors-danger)");
              reloadInvoice();
              toast.add("Fuel surcharge added to this invoice.", "var(--theme-ui-colors-success)");
            };
            const confirmRemove = async () => {
              if (!fuelRemove) return;
              const reason = fuelRemove.reason.trim();
              if (!reason) return toast.add("Give a reason - it is saved on the project.", "var(--theme-ui-colors-warning)");
              setFuelRemove({ ...fuelRemove, busy: true });
              const r: any = await setInvoiceSurcharge(invoice.id, false, reason);
              if (r?.error) {
                setFuelRemove({ ...fuelRemove, busy: false });
                return toast.add(r.error, "var(--theme-ui-colors-danger)");
              }
              let poMsg = "";
              if (fuelRemove.alsoPo) {
                const ids: string[] = Array.isArray(r?.relatedPurchaseOrderIds) ? r.relatedPurchaseOrderIds : [];
                let done = 0;
                const refused: string[] = [];
                for (const poId of ids) {
                  const pr: any = await setPurchaseOrderSurcharge(poId, "remove", reason);
                  if (pr?.error) refused.push(pr.error); else done++;
                }
                poMsg = !ids.length
                  ? " No PO on this job carries a surcharge."
                  : refused.length
                    ? ` Removed from ${done} PO${done === 1 ? "" : "s"}; ${refused.length} not changed: ${refused[0]}`
                    : ` Also removed from ${done} PO${done === 1 ? "" : "s"}.`;
              }
              setFuelRemove(null);
              reloadInvoice();
              toast.add(`Fuel surcharge removed from this invoice.${poMsg}`, poMsg.includes("not changed") ? "var(--theme-ui-colors-warning)" : "var(--theme-ui-colors-success)");
            };
            return (
              <Box id="fuel-surcharge-control" sx={{ marginTop: "8px", scrollMarginTop: "120px" }}>
                {!fuelRemove && (
                  <Flex sx={{ justifyContent: "flex-end" }}>
                    <BasicButton outline scheme="secondary" type="button" keepText onClick={() => (hasFuel ? setFuelRemove({ reason: "", alsoPo: false, busy: false }) : addBack())}>
                      {hasFuel ? "Remove fuel surcharge" : "Add fuel surcharge"}
                    </BasicButton>
                  </Flex>
                )}
                {fuelRemove && (
                  <Box sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", padding: "12px", background: "#FAFAF7" }}>
                    <div style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Remove the fuel surcharge from this invoice</div>
                    <textarea
                      value={fuelRemove.reason}
                      onChange={(e) => setFuelRemove({ ...fuelRemove, reason: e.target.value })}
                      placeholder="Reason (required) - e.g. client disputed, not seen on the quote"
                      rows={2}
                      style={{ width: "100%", boxSizing: "border-box", border: "1px solid #EBEBEB", borderRadius: 6, padding: "7px 10px", fontFamily: "Poppins", fontSize: 13 }}
                    />
                    <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 8, fontFamily: "Poppins", fontSize: 13 }}>
                      <input type="checkbox" checked={fuelRemove.alsoPo} onChange={(e) => setFuelRemove({ ...fuelRemove, alsoPo: e.target.checked })} style={{ marginTop: 3 }} />
                      <span>The provider has agreed not to charge it &ndash; also remove it from the PO. <span style={{ color: themevals.colors.dkgrey }}>Leave unticked if we still owe the provider.</span></span>
                    </label>
                    <Flex sx={{ justifyContent: "flex-end", gap: "8px", marginTop: "10px" }}>
                      <BasicButton outline scheme="secondary" type="button" keepText disabled={fuelRemove.busy} onClick={() => setFuelRemove(null)}>Cancel</BasicButton>
                      <BasicButton type="button" keepText disabled={fuelRemove.busy} onClick={confirmRemove}>{fuelRemove.busy ? "Removing\u2026" : "Remove"}</BasicButton>
                    </Flex>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Box>

        {/* 5. Totals block — Subtotal / Discount / Amount Paid / Amount Due */}
        <Flex sx={{ justifyContent: "flex-end", width: "100%", marginTop: "24px" }}>
          <Box sx={{ width: "280px" }}>
            <Flex sx={{ justifyContent: "space-between", paddingBottom: "8px" }}>
              <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" color={themevals.colors.dkgrey}>Subtotal</P>
              <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" fontWeight="500" color="#000">
                {roundedTotal?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
              </P>
            </Flex>
            {discountAmount > 0 && (
              <Flex sx={{ justifyContent: "space-between", paddingBottom: "8px" }}>
                <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" color={themevals.colors.dkgrey}>Discount</P>
                <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" fontWeight="500" color="#000">
                  -{discountAmount?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                </P>
              </Flex>
            )}
            {totalPaid > 0 && (
              <Flex sx={{ justifyContent: "space-between", paddingBottom: "8px" }}>
                <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" color={themevals.colors.dkgrey}>Amount Paid</P>
                <P fontFamily="Poppins" fontSize="11px" marginTop="0" marginBottom="0" fontWeight="500" color="#000">
                  -{totalPaid?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                </P>
              </Flex>
            )}
            <Box sx={{ borderTop: "1px solid #000", paddingTop: "8px" }}>
              <Flex sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
                <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0" marginBottom="0" color="#000">Amount Due</P>
                <P fontFamily="Poppins" fontSize="14px" fontWeight="600" marginTop="0" marginBottom="0" color="#000">
                  {amountDue?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                </P>
              </Flex>
            </Box>
          </Box>
        </Flex>

        {/* 6. Terms footnote */}
        <Box sx={{ width: "100%", marginTop: "40px", paddingTop: "20px", borderTop: `1px solid ${themevals.colors.inputOutline}` }}>
          {isCompany ? (
            /* Staff: editable per-record T&C override (Session 59, Part B).
               Client view keeps the read-only rendered terms below. */
            <TermsEditor
              docType="invoice"
              record={invoice}
              update={updateInvoice}
              onSaved={reloadInvoice}
            />
          ) : (
            <>
              <Box sx={{ fontFamily: "Poppins-Medium, Poppins, sans-serif", fontSize: "8px", letterSpacing: "0.5px", color: themevals.colors.dkgrey, textTransform: "uppercase", marginBottom: "6px" }}>
                Terms
              </Box>
              <P color={themevals.colors.dkgrey} marginBottom="0" marginTop="0" fontFamily="Poppins" fontSize="10px" lineHeight="16px" style={{ whiteSpace: "pre-wrap" }}>
                {invoice?.resolvedTerms || ""}
              </P>
            </>
          )}
        </Box>
      </Box>
      {projectPhotos
        ?.filter((p: any) => p.invoiceInclude)
        ?.reduce((acc: any[][], photo: any, idx: number) => {
          if (idx % 2 === 0) acc.push([photo]);
          else acc[acc.length - 1].push(photo);
          return acc;
        }, [])
        .map((photoGroup: any, index: number) => (
          <Flex
            key={index}
            sx={{
              maxWidth: "1000px",
              width: "100%",
              margin: "0 auto 60px auto",
              boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
              border: `1px solid ${themevals.colors.inputOutline}`,
              borderRadius: "6px",
              aspectRatio: "8.5 / 11",
              position: "relative",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              flexDirection: "column",
              gap: "40px",
              padding: "40px",
              paddingTop: "125px",
            }}
          >
            {photoGroup.map((photo: any, i: number) => (
              <Box key={i} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <img src={photo.url} style={{ maxHeight: "400px", maxWidth: "100%", objectFit: "contain" }} />
              </Box>
            ))}
          </Flex>
        ))}

      {/* ─── CC Payment Modal ─── */}
      <Modal isOpen={ccIsOpen} toggle={handleCCModalToggle} centered closeOnBackdropClick={false}>
        <Formik initialValues={{}} onSubmit={() => _submitCCPayment()}>
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            submitCount,
          }: {
            isSubmitting: boolean;
            submitCount: number;
            values: any;
            setFieldValue: (name: string, val?: any) => void;
            errors: any;
          }) => {
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={handleCCModalToggle}>
                  <ModalIcon>
                    <CreditCardIcon fill={"000"} width="20px" height="20px" />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Pay via Credit Card</ModalTitle>
                    <ModalSubtitle>{"Apply a payment to the invoice using your credit card."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <Flex sx={{ marginBottom: "30px" }}>
                    <SelectInput
                      name="paymentType"
                      label="Payment Type"
                      value={paymentType}
                      options={paymentOptions}
                      onChange={(val) => handlePaymentTypeChange(val)}
                      $customStyles={{ width: "100%" }}
                      readonly={paymentTypeReadonly}
                    />
                  </Flex>
                  {/* Show discount information if eligible */}
                  {(() => {
                    if (!invoice.quote?.acceptedDate) return null;
                    
                    const baseDate = moment(invoice.quote.acceptedDate);
                    const expiryDate = baseDate.clone().add(5, 'days');
                    const now = moment();
                    const isWithinDiscountWindow = now.isBefore(expiryDate);
                    const ccInvoiceTotal = invoice?.items?.flat()?.reduce((acc: number, item: any) => acc + item.price * item.quantity, 0) || 0;
                    const ccMeetsMinimum = ccInvoiceTotal >= 2000;
                    const ccIsRush = isRushOrder;
                    
                    if (!invoice.dueDate && isWithinDiscountWindow && ccMeetsMinimum && !ccIsRush && !clientRequiresDeposit && !invoice.discountAmount && paymentType !== "CUSTOM_DEPOSIT") {
                      // Calculate discount amounts
                      const discountPercent = paymentType === "DEPOSIT" ? EARLY_PAY.depositPct : EARLY_PAY.fullPct;
                      const discountAmount = saving(discountBase(invoice?.items), discountPercent);
                      const discountedTotal = +(total - discountAmount).toFixed(2);
                      const amountToCharge = paymentType === "DEPOSIT" ? +(discountedTotal / 2).toFixed(2) : discountedTotal;
                      
                      return (
                        <Box sx={{ 
                          marginBottom: "30px", 
                          padding: "15px", 
                          backgroundColor: "#f0f7ed", 
                          borderRadius: "6px", 
                          border: "1px solid #667E4E" 
                        }}>
                          <P marginTop="0px" marginBottom="10px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" fontWeight="600" color="#667E4E">
                            Early Payment Discounts Available
                          </P>
                          <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                            By paying {paymentType === "DEPOSIT" ? "a 50% deposit" : "in full"} now, you&apos;ll receive a{" "}
                            <Span fontWeight="600">{discountPercent}% discount</Span> ({discountAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })} savings).
                          </P>
                          <P marginTop="10px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                            Discounted invoice total: <Span fontWeight="600">{discountedTotal.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>
                            {paymentType === "DEPOSIT" && (
                              <>
                                <br />
                                50% deposit amount: <Span fontWeight="600">{amountToCharge.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>
                              </>
                            )}
                          </P>
                          <Flex sx={{
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffc107",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            marginTop: "12px",
                            fontFamily: "Poppins",
                            fontSize: "12px",
                            alignItems: "center",
                            gap: "6px"
                          }}>
                            <Box sx={{ fontSize: "14px" }}>{"\u23F0"}</Box>
                            <Box sx={{ fontWeight: "600", color: "#856404" }}>Discount valid through {expiryDate.format("MMMM D, YYYY")}.</Box>
                            <Box sx={{ color: "#856404" }}>Payment must be received by this date to qualify.</Box>
                          </Flex>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                  {/* Show partial payment info for CUSTOM_DEPOSIT */}
                  {paymentType === "CUSTOM_DEPOSIT" && (
                    <Box sx={{ 
                      marginBottom: "30px", 
                      padding: "15px", 
                      backgroundColor: "#f9f9f9", 
                      borderRadius: "6px", 
                      border: `1px solid ${themevals.colors.inputOutline}` 
                    }}>
                      <P marginTop="0px" marginBottom="10px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" fontWeight="600" color={themevals.colors.black}>
                        Partial Payment
                      </P>
                      <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                        You&apos;re paying a <Span fontWeight="600">{invoice.partialPaymentPercent || 75}% deposit</Span> on this invoice.
                        The remaining balance will be due after delivery.
                      </P>
                    </Box>
                  )}
                  <P marginTop="0px" marginBottom="30px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                    Your card will be charged a <Span fontWeight="600">{intent?.fees ? Number(intent.fees).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "$0.00"}</Span> service fee plus the{" "}
                    {(() => {
                      if (!invoice.quote?.acceptedDate) return "";
                      
                      const baseDate = moment(invoice.quote.acceptedDate);
                      const expiryDate = baseDate.clone().add(5, 'days');
                      const now = moment();
                      const isWithinDiscountWindow = now.isBefore(expiryDate);
                      
                      if (!invoice.dueDate && isWithinDiscountWindow && !invoice.discountAmount) {
                        return "discounted ";
                      }
                      return "";
                    })()}
                    invoice amount for a grand total of <Span fontWeight="600">{intent?.total ? Number(intent.total).toLocaleString("en-US", { style: "currency", currency: "USD" }) : "$0.00"}</Span>
                  </P>
                  <Box sx={{ mb: 3, p: 2, border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", background: "#fff" }}>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: "14px",
                            fontFamily: "Poppins",
                            color: "#000",
                            backgroundColor: "#fff",
                            "::placeholder": { color: themevals.colors.dkgrey },
                          },
                          invalid: {
                            color: themevals.colors.danger,
                          },
                        },
                      }}
                    />
                  </Box>
                  {/* Save card checkbox */}
                  <Flex
                    sx={{
                      alignItems: "center",
                      gap: "8px",
                      marginTop: "16px",
                      cursor: "pointer",
                    }}
                    onClick={() => setSaveCardChecked(!saveCardChecked)}
                  >
                    <Box
                      sx={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "4px",
                        border: `2px solid ${saveCardChecked ? themevals.colors.primary : themevals.colors.inputOutline}`,
                        backgroundColor: saveCardChecked ? themevals.colors.primary : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.15s ease",
                      }}
                    >
                      {saveCardChecked && (
                        <CheckMarkIcon fill="#fff" />
                      )}
                    </Box>
                    <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" color={themevals.colors.dkgrey}>
                      Save this card for future payments
                    </P>
                  </Flex>
                </ModalBody>
                <ModalFooter>
                  <BasicButton type="button" onClick={() => setCCIsOpen(false)} scheme="secondaryOutline">
                    Cancel
                  </BasicButton>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} styles={{ marginLeft: "10px" }}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}Submit Payment
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>

      {/* ACH Connect Bank Modal */}
      <Modal isOpen={achIsOpen} toggle={() => setACHIsOpen(!achIsOpen)} centered closeOnBackdropClick={false}>
        <ModalHeader toggle={() => setACHIsOpen(false)}>
          <ModalIcon>
            <BankIcon fill={"000"} width="20px" height="20px" />
          </ModalIcon>
          <ModalTitleWrapper>
            <ModalTitle>Connect Bank Account</ModalTitle>
            <ModalSubtitle>{"Connect your bank account securely using Plaid to make an ACH payment."}</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          <P marginTop="0px" marginBottom="30px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
            In order to pay via ACH, you must first securely verify your banking credentials via Plaid. Your banking information is never stored on our servers.
          </P>
          <Flex sx={{ justifyContent: "center", marginBottom: "20px" }}>{achPending && <Spinner size={24} color={themevals.colors.primary} />}</Flex>
        </ModalBody>
        <ModalFooter>
          <BasicButton type="button" onClick={() => setACHIsOpen(false)} scheme="secondaryOutline">
            Cancel
          </BasicButton>
          <PlaidLinkButton onSuccess={_handlePlaidSuccess} buttonText="Connect Your Bank" isSubmitting={achSubmitting} />
        </ModalFooter>
      </Modal>

      {/* ACH Confirmation Modal */}
      <Modal isOpen={confirmationModalOpen} toggle={handleACHConfirmationModalToggle} centered closeOnBackdropClick={false}>
        <Formik
          initialValues={{
            amount: getACHInitialAmount(),
            reference: "",
          }}
          onSubmit={(values) => _submitACHPayment({ ...values, processorToken })}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            submitCount,
          }: {
            isSubmitting: boolean;
            submitCount: number;
            values: any;
            setFieldValue: (name: string, val?: any) => void;
            errors: any;
          }) => {
            const discountDetails = getEarlyPaymentDiscountDetails();
            const baseAmount =
              (discountDetails?.amountToCharge ??
                (Number(values.amount) || 0));
            const achFee = ACH_FEE;
            const totalAmount = +(baseAmount + achFee).toFixed(2);

            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={handleACHConfirmationModalToggle}>
                  <ModalIcon>
                    <BankIcon fill={"000"} width="20px" height="20px" />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Confirm ACH Payment</ModalTitle>
                    <ModalSubtitle>{"Confirm and submit your ACH payment"}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <Box sx={{ marginBottom: "30px", padding: "15px", backgroundColor: "#f9f9f9", borderRadius: "4px", border: "1px solid #e0e0e0" }}>
                    <P marginTop="0px" marginBottom="10px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" fontWeight="600" color={themevals.colors.black}>
                      Connected Account
                    </P>
                    <P marginTop="0px" marginBottom="0" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.dkgrey}>
                      {selectedBankAccount?.name} (****{selectedBankAccount?.mask})
                    </P>
                    <P marginTop="0px" marginBottom="0" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.dkgrey}>
                      {selectedBankAccount?.type?.charAt(0).toUpperCase() + selectedBankAccount?.type?.slice(1)} Account
                    </P>
                  </Box>
                  <Flex sx={{ marginBottom: "30px" }}>
                    <SelectInput
                      name="paymentType"
                      label="Payment Type"
                      value={paymentType}
                      options={paymentOptions}
                      onChange={(val) => handleACHPaymentTypeChange(val, setFieldValue)}
                      $customStyles={{ width: "100%" }}
                      readonly={paymentTypeReadonly}
                    />
                  </Flex>

                  {/* Show discount information if within 5-day window */}
                  {discountDetails && paymentType !== "CUSTOM_DEPOSIT" && (
                    <Box
                      sx={{
                        marginBottom: "30px",
                        padding: "15px",
                        backgroundColor: "#f0f7ed",
                        borderRadius: "6px",
                        border: "1px solid #667E4E",
                      }}
                    >
                      <P marginTop="0px" marginBottom="10px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" fontWeight="600" color="#667E4E">
                        🎉 Discount Available!
                      </P>
                      <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                        You&apos;re within the 5-day discount window. By paying now, you&apos;ll receive a{" "}
                        <Span fontWeight="600">{discountDetails.discountPercent}% discount</Span> (
                        {discountDetails.discountAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}).
                      </P>
                      <P marginTop="10px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                        Discounted invoice total: <Span fontWeight="600">{discountDetails.discountedTotal.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>
                        {paymentType === "DEPOSIT" && (
                          <>
                            <br />
                            50% deposit amount: <Span fontWeight="600">{discountDetails.amountToCharge.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>
                          </>
                        )}
                      </P>
                    </Box>
                  )}

                  {/* Show partial payment info for CUSTOM_DEPOSIT */}
                  {paymentType === "CUSTOM_DEPOSIT" && (
                    <Box sx={{ 
                      marginBottom: "30px", 
                      padding: "15px", 
                      backgroundColor: "#f9f9f9", 
                      borderRadius: "6px", 
                      border: `1px solid ${themevals.colors.inputOutline}` 
                    }}>
                      <P marginTop="0px" marginBottom="10px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" fontWeight="600" color={themevals.colors.black}>
                        Partial Payment
                      </P>
                      <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                        You&apos;re paying a <Span fontWeight="600">{invoice.partialPaymentPercent || 75}% deposit</Span> on this invoice.
                        The remaining balance will be due after delivery.
                      </P>
                    </Box>
                  )}

                  <P marginTop="0px" marginBottom="30px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                    The provided banking credentials have been verified. By clicking submit you are approving an ACH transfer that includes a <Span fontWeight="600">{achFee.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>{" "}
                    non-refundable service fee plus the{" "}
                    {discountDetails && paymentType !== "CUSTOM_DEPOSIT" ? "discounted " : ""}
                    {paymentType === "CUSTOM_DEPOSIT" ? "partial " : ""}
                    invoice amount of{" "}
                    <Span fontWeight="600">{baseAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span> for a grand total of{" "}
                    <Span fontWeight="600">{totalAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>.
                  </P>

                  <TextInput
                    name={`reference`}
                    value={values.reference}
                    label="Reference (optional)"
                    $customStyles={{ marginBottom: "20px" }}
                    onChange={(val: any) => setFieldValue(`reference`, val)}
                    $errors={errors?.reference && submitCount > 0 ? errors?.reference : null}
                  />

                  {/* Save bank account checkbox */}
                  <Flex
                    sx={{
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "10px",
                      cursor: "pointer",
                    }}
                    onClick={() => setSaveBankChecked(!saveBankChecked)}
                  >
                    <Box
                      sx={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "4px",
                        border: `2px solid ${saveBankChecked ? themevals.colors.primary : themevals.colors.inputOutline}`,
                        backgroundColor: saveBankChecked ? themevals.colors.primary : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.15s ease",
                      }}
                    >
                      {saveBankChecked && (
                        <CheckMarkIcon fill="#fff" />
                      )}
                    </Box>
                    <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" color={themevals.colors.dkgrey}>
                      Save this bank account for future payments
                    </P>
                  </Flex>
                </ModalBody>
                <ModalFooter>
                  <BasicButton type="button" onClick={() => setConfirmationModalOpen(false)} scheme="secondaryOutline">
                    Cancel
                  </BasicButton>
                  <BasicButton type="submit" $submitting={isSubmitting || achSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true} styles={{ marginLeft: "10px" }}>
                    {!(isSubmitting || achSubmitting) && <CheckMarkIcon fill={"#fff"} />}
                    Submit Payment
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>

      {/* ─── "How would you like to pay?" (from View/Pay Invoice) ─── */}
      <Modal isOpen={payChooserOpen} toggle={() => { setPayChooserOpen(false); setChooserPick(null); }} centered size={"md"}>
        <ModalHeader toggle={() => { setPayChooserOpen(false); setChooserPick(null); }}>
          <ModalTitleWrapper>
            <ModalTitle>{`Pay invoice GMI-${String(invoice?.sequence ?? "").padStart(5, "0")}`}</ModalTitle>
            <ModalSubtitle>Choose how much to pay now, then card or bank (ACH).</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          {chooserPick ? (
            <Box>
              {(() => {
                const r = chooserRows.find((x: any) => x.value === chooserPick.type);
                return (
                  <P marginTop="0px" marginBottom="10px" fontFamily="Poppins" fontSize="13px" color={themevals.colors.dkgrey}>
                    {`${r?.value === "PAY_IN_FULL" && r?.eligible ? "Pay in full (5% off)" : r?.value === "DEPOSIT" ? "50% deposit (3% off)" : r?.label ?? ""}${r?.amount != null ? ` · $${r.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""} · ${chooserPick.kind === "card" ? "card (3% card fee)" : "bank / ACH ($5 fee)"}`}
                  </P>
                );
              })()}
              {savedOfKind(chooserPick.kind).map((m: any) => (
                <Flex key={m.id} onClick={() => payWithSaved(chooserPick.type, m)} sx={{ alignItems: "center", gap: "10px", padding: "10px 12px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", marginBottom: "8px", cursor: "pointer", "&:hover": { backgroundColor: "#f5f5f5" } }}>
                  {chooserPick.kind === "card" ? <CreditCardIcon fill={themevals.colors.dkgrey} width="16px" height="16px" /> : <BankIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />}
                  <Box>
                    <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500">{m.label || formatMethodLabel(m)}</P>
                    <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>{m.ownerType === "org" ? "Organization" : "Personal"}</P>
                  </Box>
                </Flex>
              ))}
              <Flex onClick={() => { const pick = chooserPick; setChooserPick(null); if (pick.kind === "card") payByCard(pick.type); else payByAch(pick.type); }} sx={{ alignItems: "center", gap: "8px", padding: "10px 12px", cursor: "pointer", color: themevals.colors.primary }}>
                <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.primary}>{chooserPick.kind === "card" ? "+ Use a new card" : "+ Connect a new bank account"}</P>
              </Flex>
              <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setChooserPick(null)}>Back</BasicButton>
            </Box>
          ) : (payFocus && chooserRows.some((x: any) => x.value === payFocus) ? chooserRows.filter((x: any) => x.value === payFocus) : chooserRows).map((r: any) => (
            <Flex key={r.value} sx={{ alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "12px 0", borderBottom: `1px solid ${themevals.colors.inputOutline}`, flexWrap: "wrap" }}>
              <Box>
                <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" fontWeight="500">{r.value === "PAY_IN_FULL" && r.eligible ? "Pay in full (5% off)" : r.value === "DEPOSIT" ? "50% deposit (3% off)" : r.label}</P>
                {r.amount != null && (
                  <P marginTop="2px" marginBottom="0px" fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey}>
                    {`Pay now: $${r.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                    {r.save ? <Span fontFamily="Poppins" fontSize="12px" color={themevals.colors.success}>{` · save $${r.save.toLocaleString("en-US", { minimumFractionDigits: 2 })}`}</Span> : null}
                  </P>
                )}
              </Box>
              <Flex sx={{ gap: "8px" }}>
                <BasicButton outline scheme="secondary" type="button" keepText onClick={() => chooseKind(r.value, "card")}>Card</BasicButton>
                <BasicButton outline scheme="secondary" type="button" keepText onClick={() => chooseKind(r.value, "ach")}>ACH</BasicButton>
              </Flex>
            </Flex>
          ))}
          {!chooserPick && payFocus && chooserRows.length > 1 && chooserRows.some((x: any) => x.value === payFocus) && (
            <P marginTop="10px" marginBottom="0px" fontFamily="Poppins" fontSize="12px">
              <Box as="span" onClick={() => setPayFocus(null)} sx={{ cursor: "pointer", textDecoration: "underline", color: themevals.colors.primary }}>See all payment options</Box>
            </P>
          )}
          {!chooserPick && <P marginTop="12px" marginBottom="0px" fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey}>
            {`Or pay later: the full $${amountDue.toLocaleString("en-US", { minimumFractionDigits: 2 })} is due on your standard terms. You can pay any time from this invoice.`}
          </P>}
        </ModalBody>
        <ModalFooter>
          <BasicButton outline scheme="secondary" type="button" keepText onClick={() => { setPayChooserOpen(false); setChooserPick(null); }}>Pay later</BasicButton>
        </ModalFooter>
      </Modal>

      {/* ─── Pay with Saved Method Confirmation Modal ─── */}
      <Modal isOpen={savedMethodPayModalOpen} toggle={() => { setSavedMethodPayModalOpen(false); setSelectedSavedMethod(null); }} centered closeOnBackdropClick={false}>
        <ModalHeader toggle={() => { setSavedMethodPayModalOpen(false); setSelectedSavedMethod(null); }}>
          <ModalIcon>
            {selectedSavedMethod?.methodType === "card" ? (
              <CreditCardIcon fill={"000"} width="20px" height="20px" />
            ) : (
              <BankIcon fill={"000"} width="20px" height="20px" />
            )}
          </ModalIcon>
          <ModalTitleWrapper>
            <ModalTitle>Confirm Payment</ModalTitle>
            <ModalSubtitle>Pay with your saved payment method</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          {selectedSavedMethod && (
            <>
              <Flex sx={{ marginBottom: "20px", padding: "12px 14px", backgroundColor: "#f9f9f9", borderRadius: "6px", border: "1px solid #e0e0e0", alignItems: "center", gap: "10px" }}>
                <Box sx={{ width: "16px", height: "16px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {selectedSavedMethod.methodType === "card" ? (
                    <CreditCardIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                  ) : (
                    <BankIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                  )}
                </Box>
                <Box>
                  <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.black}>
                    {selectedSavedMethod.label || formatMethodLabel(selectedSavedMethod)}
                  </P>
                  <P marginTop="1px" marginBottom="0px" fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>
                    {selectedSavedMethod.ownerType === "org" ? "Organization method" : "Personal method"}
                  </P>
                </Box>
              </Flex>
              {(() => {
                const isCard = selectedSavedMethod.methodType === "card";
                const discountDetails = getEarlyPaymentDiscountDetails();
                const effectiveAmount = discountDetails?.amountToCharge ?? amountDue;
                const fee = isCard ? +(effectiveAmount * 0.03).toFixed(2) : ACH_FEE;
                const totalWithFee = +(effectiveAmount + fee).toFixed(2);
                return (
                  <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
                    You are authorizing a payment of{" "}
                    <Span fontWeight="600">{effectiveAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>{" "}
                    plus a{" "}
                    <Span fontWeight="600">{fee.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>{" "}
                    service fee for a total of{" "}
                    <Span fontWeight="600">{totalWithFee.toLocaleString("en-US", { style: "currency", currency: "USD" })}</Span>.
                    {discountDetails && (
                      <>
                        <br />
                        <Span fontSize="12px" color="#667E4E" fontWeight="600">
                          Includes {discountDetails.discountPercent}% early payment discount ({discountDetails.discountAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })} savings)
                        </Span>
                      </>
                    )}
                    {!isCard && " ACH payments typically take 3-5 business days to settle."}
                  </P>
                );
              })()}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <BasicButton type="button" onClick={() => { setSavedMethodPayModalOpen(false); setSelectedSavedMethod(null); }} scheme="secondaryOutline">
            Cancel
          </BasicButton>
          <BasicButton
            type="button"
            onClick={_submitSavedMethodPayment}
            $submitting={savedMethodPaySubmitting}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{ marginLeft: "10px" }}
          >
            {!savedMethodPaySubmitting && <CheckMarkIcon fill={"#fff"} />}
            Confirm Payment
          </BasicButton>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Overview;
